import { Form } from "antd";
import BaseModalForm from "components/BaseModalForm";
import message from "components/CustomMessage";
import { isNil } from "lodash";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FieldTypes, nameRegexValidator } from "utils/formUtils";
import { duplicateNameValidator } from "utils/validatorUtil";

import {
  GoogleWorkspaceOAuthScopes,
  GoogleWorkspaceSetupInstructions,
} from "./GoogleWorkspaceSetupAlert";
import {
  GOOGLE_WORKSPACE_AUTH_TYPE,
  GOOGLE_WORKSPACE_DEFAULT_STATUS,
  GoogleWorkspaceAPI,
  GoogleWorkspaceConfig,
  GoogleWorkspaceConfigRequest,
  GwsProduct,
  emailValidator,
} from "./googleWorkspaceConstants";
import useGoogleWorkspaceSetup from "./useGoogleWorkspaceSetup";

interface GoogleWorkspaceConfigFormProps {
  configData?: GoogleWorkspaceConfig;
  onUpdateConfigComponent: () => void;
  configList?: GoogleWorkspaceConfig[];
  afterClose?: () => void;
}

export default forwardRef(function GoogleWorkspaceConfigForm(
  {
    configData,
    afterClose,
    configList = [],
    onUpdateConfigComponent,
  }: GoogleWorkspaceConfigFormProps,
  ref,
) {
  const [configId, setConfigId] = useState(configData?.id);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEdit = !isNil(configData?.id);

  // Sync configId if configData changes (e.g. parent re-renders with new data)
  useEffect(() => {
    setConfigId(configData?.id);
  }, [configData?.id]);

  const {
    setupInfo,
    productsMetadata,
    defaultSelectedProducts,
    serviceAccountClientId,
    selectedProducts,
    requiredScopes,
    setSelectedProducts,
  } = useGoogleWorkspaceSetup(
    configData?.configProperties?.aiemGwsProducts ?? [],
  );

  // Pre-select default products on create once setup info loads
  useEffect(() => {
    if (
      !isEdit &&
      defaultSelectedProducts.length > 0 &&
      selectedProducts.length === 0
    ) {
      setSelectedProducts(defaultSelectedProducts);
      form.setFieldsValue({ aiemGwsProducts: defaultSelectedProducts });
    }
  }, [
    defaultSelectedProducts,
    isEdit,
    form,
    selectedProducts.length,
    setSelectedProducts,
  ]);

  const filteredConfigList = useMemo(() => {
    if (!configData?.id) return configList;
    return configList.filter((item) => item.id !== configData.id);
  }, [configList, configData?.id]);

  const initialValues = useMemo(() => {
    if (!configData) return {};
    const { authConfig, configProperties, ...rest } =
      configData as GoogleWorkspaceConfig & {
        authConfig: Record<string, unknown>;
        configProperties: Record<string, unknown>;
      };
    return { ...rest, ...authConfig, ...configProperties };
  }, [configData]);

  const saveConfiguration = useCallback(async () => {
    setLoading(true);
    try {
      const { name, impersonationEmail, aiemGwsProducts } =
        form.getFieldsValue();
      const data: Partial<GoogleWorkspaceConfigRequest> = {
        name,
        authConfig: { impersonationEmail },
        configProperties: { aiemGwsProducts },
      };
      if (!configId) {
        data.status = GOOGLE_WORKSPACE_DEFAULT_STATUS;
        data.authType = GOOGLE_WORKSPACE_AUTH_TYPE;
        const uuid = setupInfo?.prefilledParameters?.IntegrationUUID;
        if (uuid) data.uuid = uuid;
      }
      if (configId) {
        await GoogleWorkspaceAPI.updateConfiguration(configId, data).promise;
        message.success("Configuration updated successfully");
      } else {
        const response = await GoogleWorkspaceAPI.createConfiguration(
          data as GoogleWorkspaceConfigRequest,
        ).promise;
        setConfigId(response.id);
        message.success("Configuration created successfully");
      }
      onUpdateConfigComponent();
    } finally {
      setLoading(false);
    }
  }, [form, configId, setupInfo, onUpdateConfigComponent]);

  const productOptions = useMemo(
    () => productsMetadata.map((p) => ({ label: p.label, value: p.id })),
    [productsMetadata],
  );

  const fields = useMemo(
    () => [
      {
        render: () => (
          <GoogleWorkspaceSetupInstructions
            serviceAccountClientId={serviceAccountClientId}
          />
        ),
      },
      {
        name: "name",
        label: "Configuration Name",
        NoDefaultLengthRule: true,
        required: true,
        rules: [
          nameRegexValidator,
          duplicateNameValidator({ list: filteredConfigList }),
        ],
        props: {
          autoFocus: true,
          placeholder: "e.g., Production Google Workspace",
        },
      },
      {
        name: "aiemGwsProducts",
        label: "Products",
        type: FieldTypes.SELECT,
        required: true,
        options: productOptions,
        props: {
          mode: "multiple",
          placeholder: "Select products to monitor",
          onChange: (values: GwsProduct[]) => setSelectedProducts(values),
        },
        help: "Select the Google Workspace products to collect activity data from",
      },
      {
        name: "impersonationEmail",
        label: "Super Admin Email",
        required: true,
        rules: [emailValidator],
        props: { placeholder: "admin@yourdomain.com" },
        help: "Email of a Google Workspace Super Admin for service account impersonation",
      },
      {
        render: () => (
          <GoogleWorkspaceOAuthScopes requiredScopes={requiredScopes} />
        ),
      },
    ],
    [
      filteredConfigList,
      productOptions,
      requiredScopes,
      serviceAccountClientId,
      setSelectedProducts,
    ],
  );

  return (
    <BaseModalForm
      title={
        isEdit
          ? "Edit Google Workspace Configuration"
          : "Configure Google Workspace"
      }
      ref={ref as React.Ref<any>}
      form={form}
      fields={fields}
      onOk={saveConfiguration}
      initialValues={initialValues}
      afterClose={afterClose}
      confirmLoading={loading}
      width={620}
    />
  );
});
